import React, { useState, useEffect, useRef } from 'react';
import NavBar, { PageType } from './components/NavBar';
import DashboardSeite from './components/DashboardSeite';
import UploadsSeite from './components/UploadsSeite';
import AntwortenSeite from './components/AntwortenSeite';
import ArchivSeite from './components/ArchivSeite';
import StatistikSeite from './components/StatistikSeite';
import ChatbotTestSeite from './components/ChatbotTestSeite';
import EinstellungenSeite from './components/EinstellungenSeite';
import TestgeneratorSeite from './components/TestgeneratorSeite';
import { theme } from './theme';
import { getUserRoles, getConfig, getCachedVocabConfig, prewarmConfig, UserRoles, UiWarps, ClassificationSchema, ClassificationVocabulary, FlagLanding } from './services/api';
import { cacheIsSettled } from './services/configCache';
import { CurrentUser, fetchCurrentUser, beginInteractiveLogin, signOut } from './auth/identity';
import { ClassificationSchemaProvider, defaultRowLayout, type RowLayout } from './context/ClassificationSchemaContext';
import { isKnownRole, type KnownRole } from './services/roleRegistry';
import { RoleConfigWarning } from './components/RoleConfigWarning';
import { PaletteModeProvider } from './context/PaletteModeContext';
import { ChatbotScopeProvider } from './context/ChatbotScopeContext';
import { ListDetailFlipProvider } from './context/ListDetailFlipContext';
import { initChatbotClient } from './services/chatbotApi';
import { getVersionInfo, formatBuildDate } from './services/versionInfo';
import { TicketManagerProvider, useTicketManager } from './contexts/TicketManagerContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import { RowPillPreferenceProvider } from './hooks/useRowPillVisibility';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { RueckgaengigToast } from './components/RueckgaengigToast';
import PopoutShell from './components/PopoutShell';

const STORAGE_KEY = 'smarttix_last_page';
const VALID_PAGES: PageType[] = ['dashboard', 'uploads', 'responses', 'archive', 'statistics', 'chatbot', 'admin', 'testgenerator'];
const skipAuth = process.env.REACT_APP_SKIP_AUTH === 'true';

function getInitialPage(): PageType {
  const params = new URLSearchParams(window.location.search);
  const urlPage = params.get('page') as PageType | null;
  if (urlPage && VALID_PAGES.includes(urlPage)) return urlPage;

  const stored = localStorage.getItem(STORAGE_KEY) as PageType | null;
  if (stored && VALID_PAGES.includes(stored)) return stored;

  return 'dashboard';
}

// The archive undo, wired to the ONE toast component (2026-09-08, walk fix round 4). It is its own
// component rather than markup inside App because the state it reads lives in TicketManagerProvider,
// which App itself renders — a consumer has to sit UNDER the provider. It used to be a second toast
// component with its own white-card treatment (ArchiveUndoToast, deleted): the same undo, announced
// in a different look from every other undo in the app.
const ArchiveUndo: React.FC = () => {
  const { archiveUndo, undoArchive, dismissArchiveUndo } = useTicketManager();
  return (
    <RueckgaengigToast
      visible={!!archiveUndo}
      message="Ticket archiviert"
      onUndo={undoArchive}
      // The manager clears the state on its own 5 s window; the toast's own timer is held to the
      // same figure so neither half can outlive the other.
      durationMs={5000}
      onDismiss={dismissArchiveUndo}
    />
  );
};

const App: React.FC = () => {
  // ?popout=<uuid> → this window is a single-ticket tool window (PopoutShell), not the full app.
  const popoutUuid = React.useMemo(() => new URLSearchParams(window.location.search).get('popout'), []);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [authChecked, setAuthChecked] = useState(skipAuth);
  const [activePage, setActivePage] = useState<PageType>(getInitialPage);
  const [navParams, setNavParams] = useState<Record<string, string> | null>(null);
  const [userRoles, setUserRoles] = useState<UserRoles | null>(null);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  // null = config failed OR the backend's role string is outside KNOWN_ROLES — NEVER a silently
  // assumed 'gmsb'. RoleConfigWarning renders whenever this is null; role-conditional panels
  // (EinstellungenSeite/TestgeneratorSeite) only mount once it resolves to a real KnownRole.
  // Lazy initializer (Task C, layer b): seeds from the sessionStorage vocabulary cache — a warm
  // read-through instant-paints the last resolved role on a second mount within the same tab,
  // instead of starting null every time. refreshConfig() below still always fetches and always
  // re-applies the authoritative result regardless of what this seeded.
  const [customerRole, setCustomerRole] = useState<KnownRole | null>(() => {
    const cached = getCachedVocabConfig();
    return cached?.role && isKnownRole(cached.role) ? cached.role : null;
  });
  // False until the FIRST getConfig() settles (success or rejection) — distinguishes "still
  // loading" from "resolved to no role" so RoleConfigWarning never flashes during a slow but
  // healthy config fetch. customerRole===null alone conflated both states before this landed.
  // A warm cache (Task C) seeds this TRUE immediately — the whole point of the cache is that a
  // second mount within the same tab has nothing left to wait on for its first paint. `cacheIsSettled`
  // (not a bare `!== null`, final review item #10) additionally requires the entry to carry
  // `decisionKinds` — a cache written by a pre-part-4b bundle predates that field entirely, and
  // seeding this TRUE from it would let the first paint after a deploy read the wrong row-3
  // grammar (the date, not the badge) before the fresh /config fetch has delivered the served
  // handling map that decides it. See configCache.ts's own comment for why this reads a
  // field-presence check rather than folding the field into the cache's versioning hash.
  const [configSettled, setConfigSettled] = useState(() => cacheIsSettled(getCachedVocabConfig()));
  const [uiWarps, setUiWarps] = useState<UiWarps>({
    processToAntworten: true,
    testgenProcessToAntworten: true,
    testgenUploadToUploads: true,
  });
  const [autoArchiveDays, setAutoArchiveDays] = useState(0);
  const [simulateSystemData, setSimulateSystemData] = useState(false);
  // Tenant-wide Eingangsweg row visibility (operator_config UI_SHOW_SOURCE_CHANNEL). Held here, not
  // read per host, so the main window and a pop-out cannot disagree about the same ticket.
  const [showSourceChannel, setShowSourceChannel] = useState(false);
  // Tenant-wide inline-image counting rule (operator_config UI_ATTACHMENTS_COUNT_INLINE). Seeded
  // from the cache like schema/vocabulary above: a warm load must paint the tenant's real
  // counting rule on the FIRST render, never the default followed by a correction. Held here, not
  // read per host, for the same reason as showSourceChannel — the main window and a pop-out must
  // never disagree about the same ticket's attachment count.
  const [attachmentsCountInline, setAttachmentsCountInline] = useState<boolean>(
    () => getCachedVocabConfig()?.uiAttachmentsCountInline ?? true);
  // The tenant DEFAULT „Hinweise in der Liste" set (operator_config UI_ROW_PILLS_HIDDEN, /config →
  // here → RowPillPreferenceProvider). Held as STATE rather than derived per render so the provider
  // below sees a stable array identity — every list row reads the memo built from it, and a fresh
  // array each render would rebuild that memo on every paint. NULL until /config resolves and on
  // any failure: null is "no default stated", which the provider answers with the CODE default
  // (the kinds that ship hidden) — the posture a tenant that has never been asked gets anyway.
  const [rowPillsHidden, setRowPillsHidden] = useState<string[] | null>(null);
  // Tenant-wide form-to-Excel capability. False until /config resolves (and on any failure) —
  // the two list pages key their entry button on this alone, never on a role name.
  const [formExtractionEnabled, setFormExtractionEnabled] = useState(false);
  // True once the AUTHORITATIVE /config fetch has answered (success or failure) — the capability
  // flags above (formExtractionEnabled) are never cache-seeded, so until then they read false
  // without meaning it. NEVER seeded from the cache, unlike configSettled: a warm load paints from
  // the vocabulary cache with configSettled true while this stays false. Read by the list row to
  // reserve the „Formular" pill's footprint until the capability is known (part 7 fix loop,
  // 2026-10-12).
  const [capabilitiesSettled, setCapabilitiesSettled] = useState(false);
  // Seeded from the vocabulary cache (Task C) — same read-through as customerRole above.
  const [classificationSchema, setClassificationSchema] = useState<ClassificationSchema | null>(() => getCachedVocabConfig()?.classificationSchema ?? null);
  // The role's category vocabulary as data. Null on a backend that predates the field, which is
  // exactly the legacy behaviour — no version check needed, absence IS the old contract.
  const [classificationVocabulary, setClassificationVocabulary] = useState<ClassificationVocabulary | null>(() => getCachedVocabConfig()?.classificationVocabulary ?? null);
  // The served claim-kind -> Bearbeitung handling map (core, part 4c) — seeded from the cache like
  // schema/vocabulary above, for the same instant-first-paint reason. Consumers: shared.tsx's row-3
  // decision badge (decisionKindsOf/the handling lookup) and draftKindOrder's draft-slot ordering
  // (services/draftClaims.ts, read via TicketDetail).
  const [decisionKindHandling, setDecisionKindHandling] = useState<Record<string, string>>(
    () => getCachedVocabConfig()?.decisionKindHandling ?? {});
  // The served flag landings (core generation 15; part 7 fix loop, 2026-10-12) — seeded from the
  // cache like the handling map above. Consumer: services/impliedSignals via the schema context.
  const [flagLandings, setFlagLandings] = useState<Record<string, FlagLanding>>(
    () => getCachedVocabConfig()?.flagLandings ?? {});
  const [rowLayout, setRowLayout] = useState<RowLayout>(() => {
    const cached = getCachedVocabConfig();
    if (!cached) return 'strip_label';
    const role = cached.role && isKnownRole(cached.role) ? cached.role : null;
    return cached.uiRowLayout ?? defaultRowLayout(role);
  });
  // Phase 2 Task 15 / H-4 — upload state lifted to App level so the operator
  // can navigate (Dashboard ↔ Uploads ↔ Antworten) during a long batch without
  // losing the progress indicator + the AbortController (Abbrechen would break
  // otherwise). State lives on App because both UploadsSeite and DashboardSeite
  // (Schnell-Upload) write into it; whichever Seite is mounted reads it via
  // props and re-renders the progress bar.
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);

  // Why: fetch config in parallel with auth — non-blocking, chatbot features hydrate after.
  // Wrapped so EinstellungenSeite can trigger a re-fetch after saving config changes,
  // otherwise App-level state (autoArchiveDays, uiWarps) stays stale until full reload.
  const refreshConfig = React.useCallback(() => {
    getConfig().then(cfg => {
      // THE single validation of cfg.role — App is the only place a backend role string turns
      // into (or fails to turn into) a KnownRole. Everything downstream (rowLayout default,
      // customerRole itself) keys off this one determination, never re-derives it.
      const role = cfg.role && isKnownRole(cfg.role) ? cfg.role : null;
      setChatbotEnabled(cfg.chatbotEnabled);
      setCustomerRole(role);
      setClassificationVocabulary(cfg.classificationVocabulary ?? null);
      setUiWarps(cfg.uiWarps);
      setAutoArchiveDays(cfg.autoArchiveDays ?? 0);
      setSimulateSystemData(cfg.simulateSystemData ?? false);
      setShowSourceChannel(cfg.uiShowSourceChannel ?? false);
      // ⚠ Fallback direction is TRUE here, inverted from every setter around it — see the
      // useState seed above and api.ts's AppConfig comment for why.
      setAttachmentsCountInline(cfg.uiAttachmentsCountInline ?? true);
      setFormExtractionEnabled(cfg.formExtractionEnabled ?? false);
      setCapabilitiesSettled(true);
      setRowPillsHidden(cfg.uiRowPillsHidden);
      setClassificationSchema(cfg.classificationSchema ?? null);
      setDecisionKindHandling(cfg.decisionKindHandling);
      setFlagLandings(cfg.flagLandings);
      // Operator override (UI_ROW_LAYOUT via /config) wins; else the shared role-aware default.
      setRowLayout(cfg.uiRowLayout ?? defaultRowLayout(role));
      if (cfg.chatbotEnabled) initChatbotClient(cfg.chatbotBaseUrl);
      setConfigSettled(true);
    }).catch(() => {
      // Defence-in-depth, not the primary path: the real getConfig() never rejects (its own
      // catch resolves a failure-shaped AppConfig with role: null), but this promise chain must
      // never dangle unhandled regardless — an uncaught rejection here is exactly the shape that
      // let the pre-surgery code stay silently gmsb-seeded forever (the .then() callback simply
      // never ran). Falls through to the same customerRole: null the success path would compute
      // for an unrecognised/failed role; every other piece of state stays at its already-neutral
      // useState seed.
      setCustomerRole(null);
      setConfigSettled(true);
      setCapabilitiesSettled(true);
    });
  }, []);
  useEffect(() => { refreshConfig(); }, [refreshConfig]);

  useEffect(() => {
    if (!skipAuth) {
      fetchCurrentUser().then(async u => {
        setCurrentUser(u);
        if (u?.email) {
          const roles = await getUserRoles(u.email);
          console.log('[App] Setting user roles:', roles);
          setUserRoles(roles);
        }
        setAuthChecked(true);
      });
    } else {
      // Mock roles for local development (skipAuth=true)
      setCurrentUser({ email: 'local-dev@example.com', name: 'Smarttix Dev', userId: 'dev' });
      setUserRoles({
        email: 'local-dev@example.com',
        isNormalUser: true,
        isCustomerAdmin: true,
        isAdmin: true
      });
      setAuthChecked(true);
    }
  }, []);

  // Task C, layer c: fire-and-forget /config pre-warm the moment the Anmeldung (login) screen
  // renders (authChecked && !currentUser — see the gate below). By the time the operator finishes the
  // Microsoft sign-in redirect round-trip and lands back here, the backend's cold start / DB
  // connection pool has already been absorbed by this call, so refreshConfig()'s own post-login
  // fetch (above) returns faster. prewarmConfig() itself owns "never awaited, failures silent" —
  // see its definition (api.ts) for why it deliberately bypasses coreHttp. Runs once per Anmeldung
  // render (not on every re-render) via the effect's own dependency array.
  useEffect(() => {
    if (!skipAuth && authChecked && !currentUser) prewarmConfig();
  }, [authChecked, currentUser]);

  // Why: background + keyframes moved to index.css to avoid flash-of-white before React mounts

  const navigate = (page: PageType, params?: Record<string, string>) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    url.searchParams.delete('tab');
    url.searchParams.delete('ticket');
    if (params) {
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
    }
    window.history.replaceState({}, '', url.toString());
    localStorage.setItem(STORAGE_KEY, page);
    setNavParams(params ?? null);
    setActivePage(page);
  };

  // ═══════════════════════════════════════════════════════════
  // Page renderer
  // ═══════════════════════════════════════════════════════════

  const renderPage = () => {
    switch (activePage) {
      // DashboardSeite uses its own per-row quickUploads state (different flow);
      // doesn't share the App-level uploadProgress used by UploadsSeite batch.
      case 'dashboard': return <DashboardSeite currentUser={currentUser} onNavigate={navigate} attachmentsCountInline={attachmentsCountInline} />;
      case 'uploads': return <UploadsSeite onNavigate={navigate} initialTicket={navParams?.ticket ?? null} uiWarps={uiWarps} uploadProgress={uploadProgress} setUploadProgress={setUploadProgress} uploadAbortRef={uploadAbortRef} attachmentsCountInline={attachmentsCountInline} />;
      case 'responses': return <AntwortenSeite onNavigate={navigate} initialTicket={navParams?.ticket ?? null} userRoles={userRoles} autoArchiveDays={autoArchiveDays} simulateSystemData={simulateSystemData} showSourceChannel={showSourceChannel} attachmentsCountInline={attachmentsCountInline} />;
      case 'archive': return <ArchivSeite onNavigate={navigate} showSourceChannel={showSourceChannel} attachmentsCountInline={attachmentsCountInline} />;
      case 'statistics': return (userRoles?.isAdmin || userRoles?.isCustomerAdmin)
        ? <StatistikSeite chatbotEnabled={chatbotEnabled} />
        : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
            Sie haben keinen Zugriff auf die Statistik.
          </div>;
      case 'chatbot': return (userRoles?.isAdmin || userRoles?.isCustomerAdmin)
        ? <ChatbotTestSeite />
        : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
            Sie haben keinen Zugriff auf den Chatbot-Test.
          </div>;
      // Why (#2 + ChatbotTest resolution): admin AND customeradmin can manage Einstellungen.
      // Workflow-Verhalten section + customer config visible to both; User-CRUD sub-gated to isAdmin only inside the page.
      // Spec 2026-05-27 §4 — open to any authenticated user; per-tab and
      // per-row role gating inside EinstellungenSeite does the access control.
      // customerRole===null (config failed / unknown role): EinstellungenSeite/TestgeneratorSeite
      // are inherently role-conditional throughout (CONFIG_GROUPS role gates, ROLE_DISPLAY_LABELS
      // lookup, edifact===vnb branches) — they have no neutral shape to fall back to, so neither mounts.
      // RoleConfigWarning (rendered globally below) already carries the loud signal; this text
      // explains why the page-specific tool is unavailable rather than crashing on a null role.
      case 'admin': return userRoles
        ? (customerRole
            ? <EinstellungenSeite role={customerRole} userRoles={userRoles} onConfigSaved={refreshConfig} />
            : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
                Einstellungen sind im neutralen Modus nicht verfügbar — Rollenkonfiguration unvollständig.
              </div>)
        : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
            Anmeldung erforderlich.
          </div>;
      case 'testgenerator': return userRoles?.isAdmin
        ? (customerRole
            ? <TestgeneratorSeite role={customerRole} onNavigate={navigate} uiWarps={uiWarps} />
            : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
                Testgenerator ist im neutralen Modus nicht verfügbar — Rollenkonfiguration unvollständig.
              </div>)
        : <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.textSecondary, fontSize: 14 }}>
            Sie haben keinen Zugriff auf den Testgenerator.
          </div>;
      default: return <DashboardSeite currentUser={currentUser} onNavigate={navigate} />;
    }
  };

  // Loading state while checking auth
  if (!authChecked) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.bgPage,
        fontFamily: theme.font.family,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.colors.textSecondary,
        fontSize: 14,
      }}>
        Authentifizierung wird geprüft...
      </div>
    );
  }

  // Auth gate
  if (!skipAuth && !currentUser) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.bgPage,
        fontFamily: theme.font.family,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: theme.colors.white,
          padding: theme.spacing.xl * 2,
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.card,
          textAlign: 'center',
          maxWidth: 400,
        }}>
          <img
            src="/smarttix-logo.png"
            alt="Smarttix"
            style={{ height: 40, marginBottom: theme.spacing.lg }}
          />
          <h2 style={{
            color: theme.colors.navy,
            margin: `0 0 ${theme.spacing.sm}px 0`,
            fontSize: 20,
            fontWeight: 600,
          }}>
            Willkommen bei Smarttix
          </h2>
          <p style={{
            color: theme.colors.textSecondary,
            margin: `0 0 ${theme.spacing.lg}px 0`,
            fontSize: 14,
          }}>
            Bitte melden Sie sich mit Ihrem Microsoft-Konto an.
          </p>
          <button
            onClick={() => { void beginInteractiveLogin(); }}
            style={{
              display: 'inline-block',
              padding: `${theme.spacing.md}px ${theme.spacing.xl}px`,
              fontSize: 15,
              fontWeight: 600,
              fontFamily: theme.font.family,
              color: theme.colors.white,
              backgroundColor: theme.colors.navy,
              border: 'none',
              borderRadius: theme.borderRadius.md,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
          >
            Mit Microsoft anmelden
          </button>
        </div>
      </div>
    );
  }

  // Why: user passed Entra auth but has no role in the users table — show clear message
  // instead of silently sending empty customer= requests
  if (authChecked && currentUser && !userRoles) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.bgPage,
        fontFamily: theme.font.family,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: theme.colors.white,
          padding: theme.spacing.xl * 2,
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.card,
          textAlign: 'center',
          maxWidth: 440,
        }}>
          <h2 style={{
            color: theme.colors.navy,
            margin: `0 0 ${theme.spacing.sm}px 0`,
            fontSize: 20,
            fontWeight: 600,
          }}>
            Kein Zugriff
          </h2>
          <p style={{
            color: theme.colors.textSecondary,
            margin: `0 0 ${theme.spacing.md}px 0`,
            fontSize: 14,
            lineHeight: 1.5,
          }}>
            {/* Why the address appears TWICE and as the instruction's object: authorization
                matches users.email against exactly this string, and Entra reports the
                account's canonical address no matter which alias was typed at the prompt.
                Stating only "account X failed" reads as "try another account" — a loop with
                no exit. Naming it as the string to HAND OVER is what makes the screen
                actionable without anyone knowing what an alias is. */}
            Ihr Konto ({currentUser.email}) ist angemeldet, aber es wurde keine Rolle zugewiesen.
            {' '}Bitte teilen Sie Ihrem Administrator genau diese Adresse mit:{' '}
            <strong>{currentUser.email}</strong>
          </p>
          {/* A button, not an anchor: signing out has to end the MSAL session this app holds,
              which only the identity facade can do — an href to a platform logout URL leaves the
              account cache intact and the next load silently re-authenticates. Styling is
              unchanged from the anchor it replaces (same box, same metrics), so the screen does
              not shift. */}
          <button
            type="button"
            onClick={() => { void signOut(); }}
            style={{
              display: 'inline-block',
              padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
              fontSize: 13,
              color: theme.colors.navy,
              backgroundColor: theme.colors.bgPage,
              border: `1px solid ${theme.colors.borderLight}`,
              borderRadius: theme.borderRadius.md,
              textDecoration: 'none',
              fontFamily: theme.font.family,
              cursor: 'pointer',
              lineHeight: 1.5,
            }}
          >
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <ListDetailFlipProvider>
    <PaletteModeProvider>
    <ChatbotScopeProvider>
    <ClassificationSchemaProvider value={{ schema: classificationSchema, vocabulary: classificationVocabulary, rowLayout, role: customerRole, configSettled, decisionKindHandling, flagLandings, formExtractionEnabled, capabilitiesSettled, refresh: refreshConfig }}>
    {/* The clerk's own „Hinweise in der Liste" set, fetched ONCE for the whole session (P2 Task R4)
        — inside the classification provider because the registry's kinds are derived from the
        served vocabulary, and above every list so a row never reads the preference for itself. */}
    <RowPillPreferenceProvider tenantDefault={rowPillsHidden}>
    <MaintenanceProvider>
    <TicketManagerProvider>
    {/* Loud-but-alive: visible in both the main shell and popout — customerRole is app-wide state,
        not a main-shell-only concept. Fixed-position overlay (see RoleConfigWarning), so it never
        shifts NavBar/page content as config resolves. Gated on configSettled so a slow-but-healthy
        config fetch never flashes the warning while still pending (customerRole is null both while
        loading and after a genuine failure — configSettled disambiguates). */}
    {configSettled && customerRole === null && <RoleConfigWarning />}
    {popoutUuid ? (
      <PopoutShell uuid={popoutUuid} userRoles={userRoles} simulateSystemData={simulateSystemData} showSourceChannel={showSourceChannel} attachmentsCountInline={attachmentsCountInline} />
    ) : (
    <div style={{ backgroundColor: theme.colors.bgPage, fontFamily: theme.font.family }}>
      {/* WCAG 2.4.1 — first focusable element jumps keyboard users past NavBar to main content.
          Visually-hidden until focused; reveal styles live in index.css (.skip-link class). */}
      <a href="#main" className="skip-link">Zum Inhalt springen</a>
      {/* Banner is below the skip-link in DOM (so the skip-link stays the first
          focusable element) but visually at the very top. */}
      <MaintenanceBanner />
      <NavBar activePage={activePage} onNavigate={navigate} user={currentUser} userRoles={userRoles} chatbotEnabled={chatbotEnabled} />

      <main id="main" style={{
        // Every main page shares one width (operator ruling 2026-09-28) — 'admin' (Einstellungen)
        // was the one page type still narrower; the per-page ternary is gone because there is no
        // longer a second branch left to pick between.
        maxWidth: 1200,
        margin: '0 auto',
        padding: `${theme.spacing.xl}px ${theme.spacing.lg}px ${theme.spacing.lg}px`,
        transition: 'max-width 0.3s ease',
      }}>
        {renderPage()}
      </main>

      <ArchiveUndo />

      <footer style={{
        maxWidth: 1200,
        margin: `0 auto ${theme.spacing.lg}px`,
        padding: `${theme.spacing.md}px 0`,
        fontSize: 13,
        color: theme.colors.textSecondary,
        textAlign: 'center',
        borderTop: `1px solid ${theme.colors.borderLight}`,
        fontFamily: theme.font.family,
      }}>
        <div style={{ marginBottom: 4 }}>
          Smarttix — die Energie-KI von Utility Partners Operations GmbH
        </div>
        {(() => {
          const v = getVersionInfo();
          return (
            <div style={{ color: theme.colors.textSecondary, fontSize: 12 }} title={v.sha ? `Build ${v.sha}` : undefined}>
              Version {v.version}
              {' · '}
              {formatBuildDate(v.buildDate)}
            </div>
          );
        })()}
      </footer>
    </div>
    )}
    </TicketManagerProvider>
    </MaintenanceProvider>
    </RowPillPreferenceProvider>
    </ClassificationSchemaProvider>
    </ChatbotScopeProvider>
    </PaletteModeProvider>
    </ListDetailFlipProvider>
  );
};

export default App;
